import ProductGroupSelect from '@/components/Form/Shared/ProductGroupSelect'
import { useFieldArray, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { GridHookForm, IconButton } from 'vnetwork-ui'
import AddIcons from '@/icons/AddIcons'
import DeleteOutlineIcons from '@/icons/DeleteOutlineIcons'
import InfoIcons from '@/icons/InfoIcons'
import { createKPIFormSchema } from './schema'
import { yupResolver } from '@hookform/resolvers/yup'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import { saveEmployeeKPITarget } from '../../../services'
import { KPITarget } from '../../../types'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'

export interface CreateKPIFormData {
    employee_id: number
    product_group_name: string
    new_sale_rate: number
    renewal_rate: number
    upsale_rate: number
}

interface CreateKPIProps {
    employeeId: number
    existingKPIs?: KPITarget[]
}

const CreateKPI = ({ employeeId, existingKPIs }: CreateKPIProps) => {
    const { t } = useTranslation()

    const { mutate: createKPI } = useMutationWithHandler(
        {
            mutationFn: (data: CreateKPIFormData[]) => {
                return saveEmployeeKPITarget(data)
            },
        },
        {
            reactQueryKeys: QUERY_KEYS.KPI,
            successMessage: t('kpi_created_successfully'),
            errorMessage: t('kpi_created_failed') || 'KPI creation failed',
            onSuccess: () => {
                formControl.reset()
            },
        }
    )
    const formControl = useForm<{
        employees: CreateKPIFormData[]
    }>({
        defaultValues: {
            employees: existingKPIs ?? [
                {
                    employee_id: employeeId,
                    product_group_name: '',
                    new_sale_rate: 0,
                    renewal_rate: 0,
                    upsale_rate: 0,
                },
            ],
        },
        // @ts-ignore
        resolver: yupResolver(createKPIFormSchema),
    })

    const { fields, append, remove } = useFieldArray({
        control: formControl.control,
        name: 'employees',
    })

    const selectedProductGroups = formControl
        .watch('employees')
        .map((emp) => emp.product_group_name)
        .filter(Boolean)

    // Get existing product groups to show current values if editing
    const existingProductGroups =
        existingKPIs?.map((kpi) => kpi.product_group_name) || []
    console.log('Existing KPIs:', existingKPIs)
    console.log('Existing Product Groups:', existingProductGroups)

    const buildTargetData = (data: CreateKPIFormData[]) => {
        return data.map((emp) => ({
            ...emp,
            new_sale_rate: emp.new_sale_rate / 100,
            renewal_rate: emp.renewal_rate / 100,
            upsale_rate: emp.upsale_rate / 100,
        }))
    }

    const onSubmit = async (data: { employees: CreateKPIFormData[] }) => {
        createKPI(buildTargetData(data.employees))
    }

    return (
        <>
            <div className="mb-3 p-3 bg-blue-50 border-l-4 border-blue-400 rounded-r-lg">
                <div className="flex items-start gap-3">
                    <InfoIcons className="w-4 h-4 text-primary" />
                    <div>
                        <p className="text-sm font-medium text-primary mb-1">
                            {t('kpi_notification_title')}
                        </p>
                        <p className="text-sm text-primary">
                            {t('kpi_notification_message')}
                        </p>
                    </div>
                </div>
            </div>

            <GridHookForm
                formControl={formControl}
                data={[
                    {
                        renderEditComponent: () => (
                            <div className="space-y-2">
                                {fields?.map((field, index) => {
                                    return (
                                        <div
                                            key={field.id}
                                            className="space-y-2"
                                        >
                                            {fields?.length > 1 && (
                                                <div className="flex justify-end">
                                                    <IconButton
                                                        size="small"
                                                        className="hover:text-c-error"
                                                        onClick={() =>
                                                            remove(index)
                                                        }
                                                    >
                                                        <DeleteOutlineIcons className="w-4 h-4" />
                                                    </IconButton>
                                                </div>
                                            )}
                                            <GridHookForm
                                                subForm={true}
                                                formControl={formControl}
                                                data={[
                                                    {
                                                        renderEditComponent:
                                                            () => (
                                                                <ProductGroupSelect
                                                                    showLabel
                                                                    name={`employees.${index}.product_group_name`}
                                                                    control={
                                                                        formControl.control
                                                                    }
                                                                    disabledProductGroups={selectedProductGroups?.filter(
                                                                        (
                                                                            group
                                                                        ) =>
                                                                            group !==
                                                                            formControl?.getValues(
                                                                                `employees.${index}.product_group_name`
                                                                            )
                                                                    )}
                                                                />
                                                            ),
                                                        className: 'col-span-1',
                                                    },
                                                    {
                                                        formItemProps: {
                                                            type: 'number',
                                                            name: `employees.${index}.new_sale_rate`,
                                                            label:
                                                                t(
                                                                    'new_sale_rate'
                                                                ) + ' (%)',
                                                            placeholder: '0.00',
                                                        },
                                                        className: 'col-span-1',
                                                    },
                                                    {
                                                        formItemProps: {
                                                            type: 'number',
                                                            name: `employees.${index}.renewal_rate`,
                                                            label:
                                                                t(
                                                                    'renewal_rate'
                                                                ) + ' (%)',
                                                            placeholder: '0.00',
                                                        },
                                                        className: 'col-span-1',
                                                    },
                                                    {
                                                        formItemProps: {
                                                            type: 'number',
                                                            name: `employees.${index}.upsale_rate`,
                                                            label:
                                                                t(
                                                                    'upsale_rate'
                                                                ) + ' (%)',
                                                            placeholder: '0.00',
                                                        },
                                                        className: 'col-span-1',
                                                    },
                                                ]}
                                                actions={[]}
                                            />
                                        </div>
                                    )
                                })}

                                <div className="flex justify-end">
                                    <IconButton
                                        size="small"
                                        className="border border-success"
                                        onClick={() =>
                                            append({
                                                employee_id: employeeId,
                                                product_group_name: '',
                                                new_sale_rate: 0,
                                                renewal_rate: 0,
                                                upsale_rate: 0,
                                            })
                                        }
                                    >
                                        <AddIcons className="w-4 h-4 text-success" />
                                    </IconButton>
                                </div>
                            </div>
                        ),
                        className: 'col-span-4',
                    },
                ]}
                actions={[
                    {
                        type: 'button',
                        title: t('reset'),
                        variant: 'outlined',
                        handler: () => formControl.reset(),
                    },
                    {
                        type: 'submit',
                        title: t('save_changes'),
                        variant: 'contained',
                    },
                ]}
                onSubmit={onSubmit}
            />
        </>
    )
}

export default CreateKPI
